/**
 * 從訪談建立應收(銷貨)單的小型 Modal(REQ-20260730)。
 * 只服務 customer 訪談;供應商訪談沒有應收概念,不會被呼叫到這裡。
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { useForm } from 'react-hook-form';
import {
  CreateInterviewReceivableSchema,
  calcLineAmount,
  calcTaxModeAmounts,
  toAmountString,
  toNumber,
  type CreateInterviewReceivableInput,
  type InterviewResponse,
  type SalesOrderTaxMode,
} from '@app/shared';
import { ArtworkPicker, artworkImages } from '../../components/ArtworkPicker';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Modal } from '../../components/Modal';
import {
  MultiSelectField,
  joinItems,
  optionNames,
} from '../../components/MultiSelectField';
import { formatMoney } from '../../lib/format-money';
import { usePayerNames, useSalesOrders } from '../accounting/api';
import {
  PERSONAL_CERT_KEYWORDS,
  PERSONAL_METHOD_KEYWORDS,
  matchReferenceId,
} from '../accounting/payer-defaults';
import { useCustomer } from '../customers/api';
import { OpenRemindersBanner } from '../party-reminders/OpenRemindersBanner';
import { useReferences } from '../settings/api';
import { useCreateInterviewReceivable } from './api';

const ORDER_STATUS_LABELS: Record<string, string> = {
  DRAFT: '草稿',
  POSTED: '已過帳',
  HISTORICAL: '歷史單',
};

/**
 * 這些收款憑證的客戶不開發票,稅別預設免稅(REQ-20260807002)。
 * 比對的是設定頁「收款憑證」的**名稱**而不是 id —— id 是各環境自己長出來的流水號,
 * 寫死 id 換一台機器就對不上。
 */
const EXEMPT_PAYMENT_CERT_NAMES = ['空白', '簽單', '收據'];

export interface CreateReceivableModalProps {
  open: boolean;
  onClose: () => void;
  customerId: number;
  interview: InterviewResponse;
}

/**
 * 備註預設帶入該訪談的**完整** notes(REQ-20260805001):品名 / 規格 / 材質 / 數量 /
 * 單價已經有各自的欄位,備註的角色改成「原始訪談文字備查」,截斷反而會丟資料。
 * 只在超過 zod 上限(2000)時才切,避免預設值一填進去就送不出去。
 */
function defaultNotes(notes: string | null): string | null {
  if (!notes) return null;
  const trimmed = notes.trim();
  if (!trimmed) return null;
  return trimmed.length > 2000 ? trimmed.slice(0, 2000) : trimmed;
}

/** 頂端唯讀摘要用:只是給人核對是哪一筆訪談,維持短版,不跟著備註放長。 */
function summaryText(notes: string | null): string | null {
  if (!notes) return null;
  const trimmed = notes.trim();
  if (!trimmed) return null;
  return trimmed.length > 100 ? `${trimmed.slice(0, 100)}…` : trimmed;
}

export function CreateReceivableModal(props: CreateReceivableModalProps): JSX.Element {
  const { open, onClose, customerId, interview } = props;
  const mutation = useCreateInterviewReceivable(customerId);
  const summaryNotes = summaryText(interview.notes);
  // 單位選項與設定頁的「貨品單位」同一份來源(只取啟用中的),但欄位存的是文字不是 id
  const { data: units } = useReferences('product-units', false);
  // 貨品類別同樣取設定頁的清單,只是這欄存的是 id(FK)
  const { data: categories } = useReferences('product-categories', false);
  /**
   * 加工 / 印刷顏色 / 稿件處理 / 出貨方式的常用選項(REQ-20260810001)。
   * 一律只取啟用中的,與單位欄同一個慣例:停用的選項不該出現在新單的建議清單,
   * 但因為存的是文字,舊單上的舊值仍完整顯示得出來。
   */
  const { data: processingOpts } = useReferences('processing-options', false);
  const { data: printColorOpts } = useReferences('print-color-options', false);
  const { data: artworkHandlingOpts } = useReferences('artwork-handling-options', false);
  const { data: shippingMethodOpts } = useReferences('shipping-method-options', false);
  /** 印刷面別的常用選項(REQ-20260810003)。同樣只取啟用中的 */
  const { data: printSideOpts } = useReferences('print-side-options', false);

  /**
   * 稿樣的選擇放在本地 state 而不是 `register()`:RHF 的 checkbox 一律回傳字串,
   * 而 `artworkAttachmentIds` 是 `z.array(z.number())` —— 用 register 送出必定驗證失敗。
   * REQ-20260809004 起可複選,所以存的是 id 陣列(勾選順序 = 列印排列順序)。
   */
  const [artworkAttachmentIds, setArtworkAttachmentIds] = useState<number[]>([]);

  /**
   * 有圖可挑卻一張都沒勾:送出時攔下來問一次(REQ-20260903005;
   * REQ-20260903009 從常駐的 inline 提醒改成送出才跳確認視窗)。
   * **不硬擋** —— REQ-20260809004 定的「一張都不勾就是不指定」仍然成立,
   * 不勾是合法狀態。候選圖片的判斷跟 `ArtworkPicker` 借同一個 `artworkImages()`,
   * 不在這裡自己過濾一次 mimeType。
   *
   * 被攔下來的那筆值先存在 `pendingValues`:確認視窗開著 = `pendingValues != null`,
   * 「直接儲存」時拿它走原本的 mutation 路徑。**刻意不留「已確認過」的旗標** ——
   * 那樣「確認過 → 又把圖取消勾選 → 再送出」會靜默放行。
   */
  const artworkCandidates = artworkImages(interview.attachments ?? []);
  const artworkEmpty = artworkCandidates.length > 0 && artworkAttachmentIds.length === 0;
  const [pendingValues, setPendingValues] = useState<CreateInterviewReceivableInput | null>(null);

  /** 「返回勾選」要把畫面捲回稿樣區並聚焦第一格 */
  const artworkSectionRef = useRef<HTMLDivElement>(null);

  /**
   * 加工 / 印刷顏色 / 稿件處理改為可複選(REQ-20260810001)。同樣不走 `register()`:
   * 一格對應的是「多個勾選 + 手動輸入」的集合,不是單一個 input 的值。
   * 送出時才用「、」串成一個字串併進 payload(見 `onSubmit`)—— 欄位語意與資料流
   * 都沒變,仍是 `SalesOrder.printColor` 那三個自由文字欄位。
   */
  const [processingItems, setProcessingItems] = useState<string[]>([]);
  const [printColorItems, setPrintColorItems] = useState<string[]>([]);
  const [artworkHandlingItems, setArtworkHandlingItems] = useState<string[]>([]);
  /**
   * 出貨方式(REQ-20260908003)**仍然是單選**,只是輸入元件從 `<datalist>` 換成
   * `MultiSelectField multiple={false}`,所以值也跟著改走本地 state 而不是 `register()`
   * —— 元件的對外形態是「已選項目的陣列」,不是單一個 input 的值。
   * 送出時取第一顆(見 `submitReceivable`),存進去的字串格式一個字都沒變。
   */
  const [shippingMethodItems, setShippingMethodItems] = useState<string[]>([]);

  /**
   * 重複建檔提醒(REQ-20260806005):同一筆訪談已經開過單就先講一聲。
   * **不硬擋** —— 分批出貨本來就可能對同一筆訪談開第二張單,
   * 擋掉會逼使用者繞路。作廢的單不算數。
   */
  const existingOrders = useSalesOrders({ interviewId: interview.id });
  const duplicates = (existingOrders.data?.items ?? []).filter((o) => o.status !== 'VOIDED');
  const [confirmDuplicate, setConfirmDuplicate] = useState(false);

  /**
   * 上面那份清單的「前往修改」是 `target="_blank"`,所以作廢這件事發生在**另一個分頁**。
   * 兩個分頁各有自己的 QueryClient(`app/query-client.ts` 是模組層級單例),那邊
   * 作廢後的 `invalidateQueries(['sales-orders'])` 對這邊完全沒有作用;全域又刻意
   * 關掉 `refetchOnWindowFocus`。結果是切回來時警示還停在切出去之前的資料,
   * 列著一張已經被作廢的單 —— 看起來像系統沒反應。
   *
   * 所以這裡自己補一次:分頁重新變成可見時重抓。**只在彈窗開著時掛監聽**,
   * 關掉就拆掉,否則每個開過彈窗的背景分頁都會跟著打 API。
   */
  const refetchExistingOrders = existingOrders.refetch;
  useEffect(() => {
    if (!open) return;
    const handleVisible = (): void => {
      // visibilitychange 兩個方向都會觸發,切走的那一次不必重抓
      if (document.visibilityState !== 'visible') return;
      void refetchExistingOrders();
    };
    document.addEventListener('visibilitychange', handleVisible);
    return () => document.removeEventListener('visibilitychange', handleVisible);
  }, [open, refetchExistingOrders]);

  /**
   * 送出時「哪一格是金額的權威」(REQ-20260909002)。
   *
   * REQ-20260807002 時這是一組看得見的「填單價 / 填總價」radio,控制的是**顯示哪一格**;
   * 現在四格(數量 / 單位 / 單價 / 總價)一律同時顯示、兩格雙向連動,所以它退居成
   * **看不見的內部狀態**:記住使用者最後動的是哪一格,送出時只送那一格
   * (見 `submitReceivable`)。兩格都沒填時維持 `'unit'`,行為與改動前相同。
   *
   * 為什麼還要留這個狀態:另一格的值是前端除 / 乘算出來的,`1000 ÷ 3` 存成 `333.33`
   * 再乘回去只有 `999.99`。兩格一起送的話後端會以 `totalPrice` 優先,填單價的單就會
   * 改由那個四捨五入過的推導值決定金額 —— 少的那一分錢會落到月結對帳上。
   */
  const [pricedBy, setPricedBy] = useState<'unit' | 'total'>('unit');

  /**
   * 帳款對象(REQ-20260810002)。同樣用本地 state 而不是 RHF 的 radio ——
   * 理由與價格模式相同,而且它還要驅動「顯示哪幾格」與憑證 / 收款方式的預設帶入。
   * 送出時才併進 payload(見 `onSubmit`)。
   */
  const [payerType, setPayerType] = useState<'COMPANY' | 'PERSONAL'>('COMPANY');
  /** 這個客戶以前用過的私帳對象姓名,給 datalist 當建議值(仍可手動輸入新的) */
  const { data: payerNames } = usePayerNames(customerId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateInterviewReceivableInput>({
    resolver: zodResolver(CreateInterviewReceivableSchema),
    defaultValues: {
      productName: '',
      productCategoryId: null,
      productSpec: null,
      productMaterial: null,
      productProcessing: null,
      printColor: null,
      artworkHandling: null,
      shippingMethod: null,
      printSides: null,
      qty: '',
      unitPrice: '',
      unitName: null,
      taxMode: 'EXCLUSIVE',
      payerName: null,
      paymentCertId: null,
      paymentMethodId: null,
      notes: defaultNotes(interview.notes),
    },
  });

  /**
   * 稅別預設:客戶的收款憑證是「空白 / 簽單 / 收據」時不開發票,預設免稅
   * (REQ-20260807002)。憑證清單要取**全部**(第二參數 true)—— 這裡是反查客戶
   * 身上已經設定好的憑證名稱,該憑證後來被停用時仍要對得上,漏掉會變成靜默不套用。
   */
  const { data: customerRes } = useCustomer(customerId);
  const { data: certs } = useReferences('payment-certs', true);
  // 收款方式同樣取全部:選單只列啟用中的,但客戶主檔上那筆若已被停用,
  // 「沿用客戶主檔」那行仍要顯示得出它的名字(與憑證同一個理由)。
  const { data: methods } = useReferences('payment-methods', true);
  const paymentCertName = (certs?.data ?? []).find(
    (c) => c.id === customerRes?.data.paymentCertId,
  )?.name;
  const paymentMethodName = (methods?.data ?? []).find(
    (m) => m.id === customerRes?.data.paymentMethodId,
  )?.name;
  // 只在資料第一次到位時套用一次:refetch 後再蓋一次會把使用者手動改回外加 5% 的選擇吃掉
  const taxDefaultApplied = useRef(false);
  useEffect(() => {
    if (taxDefaultApplied.current || !paymentCertName) return;
    taxDefaultApplied.current = true;
    if (EXEMPT_PAYMENT_CERT_NAMES.includes(paymentCertName)) {
      setValue('taxMode', 'EXEMPT');
    }
  }, [paymentCertName, setValue]);

  // 即時金額預覽:純粹給人看,送出的 payload 不含這三個值 ——
  // 實際入帳金額一律由後端用 Prisma.Decimal 重算(見 interviews-receivable.service.ts)。
  const preview = calcPreview(
    watch('qty'),
    watch('unitPrice'),
    // 預覽的計價規則必須與送出的一致(見 `submitReceivable`):填單價時就不能拿
    // 總價那格(自動算出來的)當基數,否則畫面顯示的與實際入帳的會分家。
    pricedBy === 'total' ? watch('totalPrice') : undefined,
    watch('taxMode'),
  );

  const qtyField = register('qty');
  const unitField = register('unitName', { setValueAs: (v) => (v === '' ? null : v) });
  /**
   * 跳格的目標(REQ-20260904002)。單價 / 總價現在是同時顯示的兩格
   * (REQ-20260909002),`priceRef` 固定指向**單價** —— 單位欄按 Enter 一律往單價跳。
   *
   * REQ-20260910001 起四格都要有 ref:Enter 一路往右走到總價,← 要能一格一格退回來。
   */
  const unitRef = useRef<HTMLInputElement | null>(null);
  const priceRef = useRef<HTMLInputElement | null>(null);
  const qtyRef = useRef<HTMLInputElement | null>(null);
  const totalPriceRef = useRef<HTMLInputElement | null>(null);
  /**
   * **兩格都 register。** REQ-20260807002 當初只 register 顯示中的那一格,是因為條件
   * 渲染下 RHF 會從殘留的 DOM 節點把舊值讀回來;現在兩格都無條件 render,那個坑不存在。
   *
   * `setValueAs` 則是必要的:`decimalString.optional()` 只吃 `undefined`,空字串會直接
   * 變成「金額格式不正確」讓整張表單送不出去。`v == null` 也要擋 —— RHF 會把
   * `defaultValues` 的 null 丟進 `setValueAs`(本專案既有的坑,見 REQ-20260807002 §一)。
   */
  const unitPriceField = register('unitPrice', {
    setValueAs: (v) => (v === '' || v == null ? undefined : v),
  });
  const totalPriceField = register('totalPrice', {
    setValueAs: (v) => (v === '' || v == null ? undefined : v),
  });

  /**
   * 數量 / 單價改了 → 總價跟著算(比照進貨管理 `PurchaseOrderForm` 的 `setLinePricing`)。
   * 公式走 `calcLineAmount()`,不自己再寫一套。任一格是空的就把總價清成空字串 ——
   * 寫 `NaN` 或 `'0'` 進去會讓使用者以為系統算出了一個金額。
   *
   * **只寫「另一格」**:回寫到使用者正在打字的那一格會吃掉按鍵(打到一半游標跳格)。
   */
  const syncTotalPrice = (qty: string | undefined, unitPrice: string | undefined): void => {
    setValue('totalPrice', !qty || !unitPrice ? '' : calcLineAmount({ qty, unitPrice }));
  };

  /**
   * 反過來:填總價 → 反推單價 = 總價 ÷ 數量,取兩位小數對齊 DB 的 `DECIMAL(14,2)`。
   * 數量是 0 或空字串時**不做除法** —— 單價維持原值,輸入框照樣打得動,不會卡出
   * `Infinity` / `NaN`。
   *
   * ⚠️ 這個回推值只是**給人看的參考值**,不會被送出去(見 `submitReceivable`):
   * 填總價時總價才是金額的權威,`1000 ÷ 3 = 333.33` 乘回去是 999.99
   * (REQ-20260807002 §二 的既有結論,本次一個字都沒改)。
   */
  const syncUnitPrice = (qty: string | undefined, totalPrice: string | undefined): void => {
    if (toNumber(qty) === 0) return;
    setValue('unitPrice', toAmountString(toNumber(totalPrice) / toNumber(qty)));
  };

  /**
   * 數量改了 → 算**非權威**的那一格,權威那格原樣保留。
   *
   * 不能一律重算總價:總價先填、數量後填(合法的填單順序)時,`syncTotalPrice()`
   * 會因為單價還是空的而把總價清成空字串 —— 使用者剛談好的金額當場消失,
   * 而且兩格都空之後連送都送不出去(zod refine 擋下)。
   */
  const syncPriceForQty = (qty: string): void => {
    if (pricedBy === 'total') {
      syncUnitPrice(qty, watch('totalPrice'));
      return;
    }
    syncTotalPrice(qty, watch('unitPrice'));
  };

  /**
   * 切換帳款對象(REQ-20260810002)。
   *  - 切到個人:憑證 / 收款方式**預設**帶入名稱含「個人 / 不開發票」「現金 / 個人」的選項。
   *    找不到就留空,不強制、也不報錯 —— 兩格永遠可以手動改。
   *  - 切回公司:三格一起清成 null = 沿用客戶主檔(公司單的既有行為),
   *    否則剛剛帶進去的「不開發票」會靜默留在一張公帳單上。
   */
  const changePayerType = (next: 'COMPANY' | 'PERSONAL'): void => {
    setPayerType(next);
    if (next === 'COMPANY') {
      setValue('payerName', null);
      setValue('paymentCertId', null);
      setValue('paymentMethodId', null);
      return;
    }
    const certId = matchReferenceId(certs?.data, PERSONAL_CERT_KEYWORDS);
    const methodId = matchReferenceId(methods?.data, PERSONAL_METHOD_KEYWORDS);
    if (certId != null) setValue('paymentCertId', certId);
    if (methodId != null) setValue('paymentMethodId', methodId);
  };

  const handleClose = (): void => {
    mutation.reset();
    reset();
    setArtworkAttachmentIds([]);
    setProcessingItems([]);
    setPrintColorItems([]);
    setArtworkHandlingItems([]);
    setShippingMethodItems([]);
    setConfirmDuplicate(false);
    setPendingValues(null);
    setPricedBy('unit');
    setPayerType('COMPANY');
    onClose();
  };

  /**
   * 送出的守門(REQ-20260903009)。攔在 RHF `handleSubmit(onSubmit)` 的成功回呼開頭 ——
   * 欄位驗證都過了才輪到這一關,使用者不會一次被兩種錯誤訊息夾擊。
   */
  const onSubmit = (values: CreateInterviewReceivableInput): void => {
    if (artworkEmpty) {
      setPendingValues(values);
      return;
    }
    submitReceivable(values);
  };

  const submitReceivable = (values: CreateInterviewReceivableInput): void => {
    mutation.mutate({
      interviewId: interview.id,
      input: {
        ...values,
        /*
          單價 / 總價**只送權威的那一格**(REQ-20260909002)。畫面上兩格現在都有值
          (另一格是自動算出來的),兩格都送的話後端會以 `totalPrice` 優先
          (`interviews-receivable.service.ts` 的 `useTotalPrice`)—— 使用者填的是單價,
          金額卻改由「單價 × 數量」四捨五入後的推導值決定。
          填單價就純送單價、填總價就純送總價,後端行為與 API 契約完全不變。
        */
        ...(pricedBy === 'total' ? { unitPrice: undefined } : { totalPrice: undefined }),
        payerType,
        // 公司公帳:三格一律送 null(= 沿用客戶主檔)。切換過去又切回來時,
        // 表單裡可能還留著剛剛個人模式填的值,而那幾格已經不在畫面上了。
        ...(payerType === 'COMPANY'
          ? { payerName: null, paymentCertId: null, paymentMethodId: null }
          : {}),
        productProcessing: joinItems(processingItems),
        printColor: joinItems(printColorItems),
        artworkHandling: joinItems(artworkHandlingItems),
        // 單選:取第一顆或 null。**不能用 `joinItems()`** —— 那是複選那幾格
        // 「用『、』串起來」的語意,這一格永遠只有一個值(REQ-20260908003)
        shippingMethod: shippingMethodItems[0] ?? null,
        artworkAttachmentIds,
      },
    });
  };

  const errorList = Object.entries(errors)
    .map(([k, v]) => [k, (v as { message?: string } | undefined)?.message])
    .filter((x): x is [string, string] => typeof x[1] === 'string');

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="新增應收單"
      widthClass="max-w-lg"
      /*
        確認視窗開著時把這一層的 ESC 關掉(REQ-20260903009)。兩層的 ESC handler 都掛在
        `document` 上,不關的話在確認視窗按一次 ESC 會連整張應收單表單一起收掉 ——
        使用者剛 key 的資料全沒。
      */
      dismissOnEsc={pendingValues == null}
    >
      {/*
       * 單號放最上面讓使用者一眼看到。**建立前一定是空的** ——
       * 單號是後端在 insert 當下才決定的當日流水號(還可能因為撞號重算重試,
       * 見 CLAUDE.md §6.3),前端自己算或預測出來的號碼有很大機會跟實際存進 DB 的
       * 不一樣,那比留白更糟。這裡沿用 mutation.data,與下方成功訊息同一份資料。
       */}
      <div
        className="mb-3 flex items-baseline gap-2 border-b border-slate-200 pb-2 text-sm"
        data-testid="receivable-order-no"
      >
        <span className="text-slate-500">單號</span>
        {mutation.isSuccess ? (
          <strong className="font-mono text-slate-900">{mutation.data.orderNo}</strong>
        ) : (
          <span className="text-slate-400">建立後帶入</span>
        )}
      </div>

      {mutation.isSuccess ? (
        <div className="grid gap-4">
          <div className="bg-emerald-50 text-emerald-800 text-sm p-3 rounded" role="status">
            應收單建立成功,單號：<strong>{mutation.data.orderNo}</strong>
            <br />
            金額(含稅)：{mutation.data.total}
          </div>
          <div className="flex gap-2 justify-center">
            <a
              href={`/sales-orders/${mutation.data.orderId}/print`}
              target="_blank"
              rel="noreferrer"
              className="border border-blue-600 text-blue-700 rounded px-3 py-1.5 hover:bg-blue-50"
              data-testid="receivable-print-link"
            >
              查看 / 列印藝品印刷訂單
            </a>
            <button
              type="button"
              onClick={handleClose}
              className="bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700"
              data-testid="receivable-close-btn"
            >
              關閉
            </button>
          </div>
        </div>
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="grid gap-3"
          data-testid="receivable-form"
        >
          {/* 這家客戶「下次要注意的事」(REQ-20260918002)。唯讀、可收合、**不擋送出** ——
              提醒的是填單前要知道的規則(顏色 / 厚度 / 價格),不是送出前的防呆。 */}
          <OpenRemindersBanner ownerType="customer" ownerId={customerId} />
          {mutation.isError ? (
            <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
              建立失敗：
              {mutation.error instanceof Error ? mutation.error.message : '未知錯誤'}
            </div>
          ) : null}
          {errorList.length > 0 ? (
            <div role="alert" className="bg-red-50 text-red-700 text-sm p-2 rounded">
              {errorList.map(([k, m]) => (
                <div key={k}>
                  {k}: {m}
                </div>
              ))}
            </div>
          ) : null}

          {/* 唯讀:讓使用者建單前先核對是哪一筆訪談,不是可編輯欄位 */}
          <div
            className="bg-slate-50 border border-slate-200 rounded p-2 text-sm text-slate-600 grid gap-0.5"
            data-testid="receivable-interview-summary"
          >
            {interview.contact ? <div>聯絡人:{interview.contact}</div> : null}
            <div>訪談日期:{interview.visitedAt ? formatDate(interview.visitedAt) : '未指定日期'}</div>
            {summaryNotes ? <div className="whitespace-pre-wrap">內容:{summaryNotes}</div> : null}
          </div>

          {/* 這筆訪談已經開過單:提醒 + 要求再確認一次,但不擋(分批出貨是合法情境) */}
          {duplicates.length > 0 ? (
            <div
              role="alert"
              className="bg-amber-50 border border-amber-300 rounded p-2 text-sm text-amber-900 grid gap-1"
              data-testid="receivable-duplicate-warning"
            >
              <div className="font-medium">這筆訪談已經建過應收單:</div>
              <ul className="grid gap-0.5">
                {duplicates.map((o) => (
                  <li key={o.id}>
                    <span className="font-mono">{o.orderNo}</span>(
                    {ORDER_STATUS_LABELS[o.status] ?? o.status}
                    ,含稅 {formatMoney(o.total)}){' '}
                    {/* 建錯了要改的入口(REQ-20260811001)。開新分頁 —— 這張建單表單
                        才填到一半,不能被導航蓋掉 */}
                    <a
                      href={`/accounting?tab=sales&editOrderId=${o.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:underline"
                      data-testid={`receivable-duplicate-edit-link-${o.id}`}
                    >
                      前往修改
                    </a>
                  </li>
                ))}
              </ul>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={confirmDuplicate}
                  onChange={(e) => setConfirmDuplicate(e.target.checked)}
                  data-testid="receivable-duplicate-confirm"
                />
                我確認仍要建立(可能重複)
              </label>
            </div>
          ) : null}

          {/*
            帳款抬頭 / 對象(REQ-20260810002)。放在最上面:它決定這張單進不進
            公司月結對帳單,是先於品名金額的分流決定。
          */}
          <fieldset className="grid gap-2 rounded border border-slate-200 p-2" data-testid="receivable-payer">
            <legend className="text-sm text-slate-700">帳款抬頭 / 對象</legend>
            <div className="flex gap-4 text-sm text-slate-700">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="receivable-payer-type"
                  checked={payerType === 'COMPANY'}
                  onChange={() => changePayerType('COMPANY')}
                  data-testid="receivable-payer-type-company"
                />
                公司公帳
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="receivable-payer-type"
                  checked={payerType === 'PERSONAL'}
                  onChange={() => changePayerType('PERSONAL')}
                  data-testid="receivable-payer-type-personal"
                />
                個人(私帳)
              </label>
            </div>

            {payerType === 'COMPANY' ? (
              /* 公司公帳沿用客戶主檔,不重複輸入 —— 顯示帶出來的值讓使用者核對 */
              <div className="text-sm text-slate-500" data-testid="receivable-payer-company-hint">
                沿用客戶主檔:{customerRes?.data.simpleName ?? '—'}
                (憑證 {paymentCertName ?? '—'} / 收款方式 {paymentMethodName ?? '—'})
              </div>
            ) : (
              /*
                只在「個人」時 render 這三格,也只有這時候才 register ——
                無條件 register 的話,RHF 會從已被移除的欄位殘留 DOM 讀回舊值
                (REQ-20260807002 單價 / 總價那格踩過同一個坑)。
              */
              <div className="grid gap-2">
                <label className="grid gap-1">
                  <span className="text-sm text-slate-700">實際收款對象 *</span>
                  <input
                    type="text"
                    list="receivable-payer-name-options"
                    {...register('payerName', { setValueAs: (v) => (v === '' ? null : v) })}
                    className={inputCls}
                    placeholder="例 黃勝彬(可直接輸入,不必先建檔)"
                    data-testid="receivable-payer-name"
                  />
                  {/* 這個客戶用過的姓名當建議值;沿用單位欄那套 input + datalist,不新開一張表 */}
                  <datalist id="receivable-payer-name-options">
                    {(payerNames ?? []).map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                </label>

                <div className="grid gap-2 grid-cols-2">
                  <label className="grid gap-1">
                    <span className="text-sm text-slate-700">收執憑證</span>
                    <select
                      {...register('paymentCertId', {
                        setValueAs: (v) => (v == null || v === '' ? null : Number(v)),
                      })}
                      className={inputCls}
                      data-testid="receivable-payment-cert"
                    >
                      <option value="">沿用客戶主檔</option>
                      {(certs?.data ?? [])
                        .filter((c) => c.isActive)
                        .map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                    </select>
                  </label>

                  <label className="grid gap-1">
                    <span className="text-sm text-slate-700">收款方式</span>
                    <select
                      {...register('paymentMethodId', {
                        setValueAs: (v) => (v == null || v === '' ? null : Number(v)),
                      })}
                      className={inputCls}
                      data-testid="receivable-payment-method"
                    >
                      <option value="">沿用客戶主檔</option>
                      {(methods?.data ?? [])
                        .filter((m) => m.isActive)
                        .map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                    </select>
                  </label>
                </div>
              </div>
            )}
          </fieldset>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">貨品類別</span>
            <select
              {...register('productCategoryId', {
                // `v == null` 要擋:預設值是 null,而 `Number(null)` 是 0,
                // 會被 zod 的 `.positive()` 擋下來 —— 沒選類別就送不出單。
                // 與 CustomerForm 的 `nullableNumber()` 同一套防呆。
                setValueAs: (v) => (v == null || v === '' ? null : Number(v)),
              })}
              className={inputCls}
              data-testid="receivable-product-category"
            >
              <option value="">—</option>
              {(categories?.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">品名</span>
            <input
              type="text"
              {...register('productName')}
              className={inputCls}
              data-testid="receivable-product-name"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">規格</span>
            <input
              type="text"
              {...register('productSpec', { setValueAs: (v) => (v === '' ? null : v) })}
              className={inputCls}
              data-testid="receivable-product-spec"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">材質</span>
            <input
              type="text"
              {...register('productMaterial', { setValueAs: (v) => (v === '' ? null : v) })}
              className={inputCls}
              data-testid="receivable-product-material"
            />
          </label>

          <MultiSelectField
            label="加工"
            testId="receivable-product-processing"
            options={optionNames(processingOpts?.data)}
            selected={processingItems}
            onChange={setProcessingItems}
          />

          <MultiSelectField
            label="印刷顏色"
            testId="receivable-print-color"
            options={optionNames(printColorOpts?.data)}
            selected={printColorItems}
            onChange={setPrintColorItems}
          />

          {/*
            印刷面別(REQ-20260810003)**單選**:一張單的印刷面別只會是其中一種,
            做成複選反而會產生「單面、雙面」這種自相矛盾的值 —— 這與出貨方式
            不做複選是同一個理由,所以用原生 `<input list>` + `<datalist>`,
            不套上面那個複選元件。清單是空的時候就退化成普通文字框,不擋建單。
            ⚠️ 出貨方式已於 REQ-20260908003 改用 `MultiSelectField multiple={false}`
            (datalist 會用現值過濾清單,選過之後就換不掉);這一格**單選的理由不變**、
            也有同樣的手感問題,但使用者沒提到,本次刻意不動(該 REQ 的「已知 gap」)。
          */}
          <label className="grid gap-1">
            <span className="text-sm text-slate-700">印刷面別</span>
            <input
              type="text"
              list="receivable-print-sides-options"
              {...register('printSides', { setValueAs: (v) => (v === '' ? null : v) })}
              className={inputCls}
              placeholder="例 單面 / 雙面(可手動輸入)"
              data-testid="receivable-print-sides"
            />
            <datalist id="receivable-print-sides-options">
              {(printSideOpts?.data ?? []).map((o) => (
                <option key={o.id} value={o.name} />
              ))}
            </datalist>
          </label>

          <MultiSelectField
            label="稿件處理"
            testId="receivable-artwork-handling"
            options={optionNames(artworkHandlingOpts?.data)}
            selected={artworkHandlingItems}
            onChange={setArtworkHandlingItems}
          />

          {/*
            出貨方式**仍然維持單選**(需求圖面標註的「不可複選但可手動 KEY IN」沒有被推翻),
            但不再用 `<datalist>`(REQ-20260908003):Chrome 會**拿輸入框當下的值去過濾
            建議清單** —— 已經選了「自取」之後再按右邊的 ▼,清單只剩「自取」一項,
            看起來就像按了沒反應,非得先把字刪掉才選得到別的。這是原生行為,
            改 CSS 或加 onClick 都繞不掉,只能換元件。
            `MultiSelectField multiple={false}`:點選項是取代、chip 最多一顆、
            下面的「其他」照樣手動 key in。
          */}
          <MultiSelectField
            label="出貨方式"
            testId="receivable-shipping-method"
            multiple={false}
            options={optionNames(shippingMethodOpts?.data)}
            selected={shippingMethodItems}
            onChange={setShippingMethodItems}
          />

          {/*
            數量 / 單位 / 單價 / 總價四格同時顯示,單價 ↔ 總價雙向連動
            (REQ-20260909002,比照會計「進貨管理」的品項列)。
            Tailwind 的 `grid-cols-*` 是 `minmax(0, 1fr)`,四格會平均縮到 Modal 寬度內,
            不會被 input 的固有最小寬撐爆(與改動前的 `grid-cols-3` 同一個機制)。
          */}
          <div className="grid gap-3 grid-cols-4">
            <label className="grid gap-1">
              <span className="text-sm text-slate-700">數量 *</span>
              <input
                type="text"
                inputMode="decimal"
                {...qtyField}
                ref={(el) => {
                  qtyField.ref(el);
                  qtyRef.current = el;
                }}
                onChange={(e) => {
                  e.target.value = onlyDecimal(e.target.value);
                  void qtyField.onChange(e);
                  syncPriceForQty(e.target.value);
                }}
                onKeyDown={(e) => focusNextOnKey(e, ['Enter', 'ArrowDown', 'ArrowRight'], unitRef)}
                className={inputCls}
                placeholder="例 1 或 1.5"
                data-testid="receivable-qty"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-sm text-slate-700">單位</span>
              <input
                type="text"
                list="receivable-unit-options"
                {...unitField}
                ref={(el) => {
                  unitField.ref(el);
                  unitRef.current = el;
                }}
                onKeyDown={(e) => {
                  focusNextOnKey(e, ['Enter', 'ArrowRight'], priceRef);
                  focusPrevOnKey(e, ['ArrowLeft'], qtyRef);
                }}
                className={inputCls}
                data-testid="receivable-unit-name"
              />
              <datalist id="receivable-unit-options">
                {(units?.data ?? []).map((u) => (
                  <option key={u.id} value={u.name} />
                ))}
              </datalist>
            </label>

            {/*
              兩格都沒有星號:它們是**擇一**必填(zod 的 refine 擋的是「兩格都空」),
              兩格都掛星號會讓人以為兩格都要填。
            */}
            <label className="grid gap-1">
              <span className="text-sm text-slate-700">單價</span>
              <input
                type="text"
                inputMode="decimal"
                {...unitPriceField}
                ref={(el) => {
                  unitPriceField.ref(el);
                  priceRef.current = el;
                }}
                onChange={(e) => {
                  e.target.value = onlyDecimal(e.target.value);
                  void unitPriceField.onChange(e);
                  setPricedBy('unit');
                  syncTotalPrice(watch('qty'), e.target.value);
                }}
                onKeyDown={(e) => {
                  focusNextOnKey(e, ['Enter', 'ArrowRight'], totalPriceRef);
                  focusPrevOnKey(e, ['ArrowLeft'], unitRef);
                }}
                className={inputCls}
                placeholder="例 1000 或 0.18"
                data-testid="receivable-unit-price"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-sm text-slate-700">總價</span>
              <input
                type="text"
                inputMode="decimal"
                {...totalPriceField}
                ref={(el) => {
                  totalPriceField.ref(el);
                  totalPriceRef.current = el;
                }}
                onChange={(e) => {
                  e.target.value = onlyDecimal(e.target.value);
                  void totalPriceField.onChange(e);
                  setPricedBy('total');
                  syncUnitPrice(watch('qty'), e.target.value);
                }}
                onKeyDown={(e) => focusPrevOnKey(e, ['ArrowLeft'], priceRef)}
                className={inputCls}
                placeholder="例 3000"
                data-testid="receivable-total-price"
              />
            </label>
          </div>

          {/* 四格是「填一格、另一格自動算」,不講的話使用者會以為兩格都得自己填 */}
          <div className="text-sm text-slate-500 -mt-1">單價與總價填一格,另一格自動算</div>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">稅別</span>
            <select
              {...register('taxMode')}
              className={inputCls}
              data-testid="receivable-tax-mode"
            >
              <option value="EXCLUSIVE">外加 5%</option>
              <option value="INCLUSIVE">內含</option>
              <option value="EXEMPT">免稅</option>
            </select>
          </label>

          {/*
            稿樣:沒有圖片附件時整塊不顯示(元件自己判斷,出貨管理那張表單同一套規則)。
            包一層是為了「返回勾選」時捲回這裡並聚焦第一格(REQ-20260903009)。
          */}
          <div ref={artworkSectionRef}>
            <ArtworkPicker
              attachments={interview.attachments ?? []}
              selected={artworkAttachmentIds}
              onChange={setArtworkAttachmentIds}
              testId="receivable-artwork"
            />
          </div>

          {/* 唯讀試算,不是欄位;金額以後端算出的為準 */}
          <div
            className="bg-slate-50 border border-slate-200 rounded p-2 text-sm text-slate-700 grid gap-0.5"
            data-testid="receivable-amount-preview"
          >
            {/* 「內含」時未稅是由含稅金額反推的,不等於 數量 × 單價,所以標題不寫算式 */}
            <div className="flex justify-between">
              <span>未稅金額</span>
              <span>{formatMoney(preview.subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>稅額</span>
              <span>{formatMoney(preview.tax)}</span>
            </div>
            <div className="flex justify-between font-medium text-slate-900">
              <span>總計</span>
              <span data-testid="receivable-preview-total">{formatMoney(preview.total)}</span>
            </div>
          </div>

          <label className="grid gap-1">
            <span className="text-sm text-slate-700">備註</span>
            <textarea
              {...register('notes', { setValueAs: (v) => (v === '' ? null : v) })}
              className={inputCls}
              rows={6}
              data-testid="receivable-notes"
            />
          </label>

          <div className="flex gap-2 justify-center">
            <button
              type="submit"
              disabled={mutation.isPending || (duplicates.length > 0 && !confirmDuplicate)}
              className="bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
              data-testid="receivable-submit"
            >
              {mutation.isPending ? '建立中…' : '建立'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="border rounded px-3 py-1.5 hover:bg-slate-100"
            >
              取消
            </button>
          </div>
        </form>
      )}

      {/* 有圖可挑卻一張都沒勾時的送出確認(REQ-20260903009) */}
      <ConfirmDialog
        open={pendingValues != null}
        title="尚未勾選商品稿樣"
        message="系統偵測到您已上傳商品稿樣,但尚未勾選帶入訂單。請確認是否要直接儲存?"
        confirmLabel="直接儲存"
        cancelLabel="返回勾選"
        tone="primary"
        onConfirm={() => {
          if (pendingValues) submitReceivable(pendingValues);
        }}
        onClose={() => setPendingValues(null)}
        onCancel={() => {
          // jsdom 沒有實作 scrollIntoView,直接呼叫會在測試裡丟 TypeError
          artworkSectionRef.current?.scrollIntoView?.({ block: 'center' });
          artworkSectionRef.current
            ?.querySelector<HTMLInputElement>('input[type="checkbox"]')
            ?.focus();
        }}
      />
    </Modal>
  );
}

const inputCls =
  'w-full border rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-400';

/**
 * 「數量 → 單位 → 單價 / 總價」那一排的順向跳格(REQ-20260904002)。
 * DOM 順序本來就是這三格,Tab 走得對,缺的是方向鍵與 Enter。
 *
 *  - **組字中一律不處理**:用注音打「才」「式」時的 Enter 是選字,不是跳格。
 *  - `ArrowRight` 只有游標停在字尾、且沒有選取範圍時才跳格;
 *    游標在字中間時維持原生行為,否則打錯的字就移不回去改了。
 *  - Enter 一定要 `preventDefault()` —— 文字輸入框的 Enter 會觸發整張表單的隱含送出。
 *  - 焦點延到下一個 frame 才移:單位欄那顆 Enter 同時也是 datalist 的「選定」,
 *    當場搶走焦點會吃掉剛選到的值。
 *
 * `keys` 由呼叫端給:單位欄的 ↑ / ↓ 要原封不動留給原生 datalist,不能攔。
 */
function focusNextOnKey(
  e: KeyboardEvent<HTMLInputElement>,
  keys: string[],
  next: RefObject<HTMLInputElement>,
): void {
  if (e.nativeEvent.isComposing) return;
  if (!keys.includes(e.key)) return;
  const el = e.currentTarget;
  if (
    e.key === 'ArrowRight' &&
    (el.selectionStart !== el.selectionEnd || el.selectionStart !== el.value.length)
  ) {
    return;
  }
  e.preventDefault();
  requestAnimationFrame(() => next.current?.focus());
}

/**
 * `focusNextOnKey()` 的鏡像:同一排的**反向**跳格(REQ-20260910001)。
 *
 * 規則逐條對稱,只有邊界條件相反 —— `ArrowLeft` 只有游標停在**最左邊**、
 * 且沒有選取範圍時才跳格;游標在字中間時維持原生左移,否則打錯的字就移不回去改了。
 * `isComposing`、`preventDefault()` 與延後一幀的理由與順向那支相同,不再重述。
 */
function focusPrevOnKey(
  e: KeyboardEvent<HTMLInputElement>,
  keys: string[],
  prev: RefObject<HTMLInputElement>,
): void {
  if (e.nativeEvent.isComposing) return;
  if (!keys.includes(e.key)) return;
  const el = e.currentTarget;
  if (e.key === 'ArrowLeft' && (el.selectionStart !== el.selectionEnd || el.selectionStart !== 0)) {
    return;
  }
  e.preventDefault();
  requestAnimationFrame(() => prev.current?.focus());
}

/**
 * 只留數字與一個小數點。單價要能打 0.18,數量既有行為也允許 1.5,兩格用同一套。
 * 這只是輸入體驗上的擋門,真正的格式驗證仍在 zod(送出時) 與後端。
 */
function onlyDecimal(v: string): string {
  const cleaned = v.replace(/[^0-9.]/g, '');
  const dot = cleaned.indexOf('.');
  if (dot === -1) return cleaned;
  return cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
}

/** 空字串 / 打到一半的 "1." / 非數字 一律當 0,預覽不該因為輸入中途而炸掉。 */
function toAmount(v: unknown): number {
  if (typeof v !== 'string') return 0;
  const s = v.trim();
  if (!/^\d+(\.\d+)?$/.test(s)) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

/**
 * 前端預覽用的金額試算。算式與後端同一份(`@app/shared` 的 `calcTaxModeAmounts()`,
 * 後端是同名的 Decimal 版),避免畫面顯示與入帳金額分家。
 * 這裡的結果不會被送出、也不會入帳,只求即時。
 */
function calcPreview(
  qty: unknown,
  unitPrice: unknown,
  totalPrice: unknown,
  taxMode: unknown,
): { subtotal: number; tax: number; total: number } {
  // 順序與後端一致(見 interviews-receivable.service.ts):有總價就以總價為計價基數,
  // 不用「總價 ÷ 數量」四捨五入後的單價回乘,否則預覽會跟入帳金額差一點。
  const gross =
    totalPrice != null && totalPrice !== ''
      ? toAmount(totalPrice)
      : toAmount(qty) * toAmount(unitPrice);
  const amounts = calcTaxModeAmounts({
    gross,
    taxMode: (taxMode as SalesOrderTaxMode | undefined) ?? 'EXCLUSIVE',
  });
  return {
    subtotal: Number(amounts.subtotal),
    tax: Number(amounts.tax),
    total: Number(amounts.total),
  };
}

/**
 * 與 InterviewTimeline 的訪談日期顯示一致(本地時區,不用 iso.slice(0,10))。
 * 沒有直接 import 它的同名 function:InterviewTimeline 已經 import 本檔,會變成循環相依。
 */
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return iso;
  }
}
